import React, { useCallback } from 'react'
import {
  Box,
  Drawer,
  Grid,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'

import useAxios from '../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../constants/global'
import { Link, useNavigate } from 'react-router-dom'
import {
  EOrderStatus,
  IOrder,
  IOrderItem,
  OrderShippingProvider,
  OrderSummary,
} from '../../../../../types/order'
import PageUrls from '../../../../../constants/page-urls'
import TrackingProviderImage from '../../../../../components/TrackingProviderImage'
import OrderTag from '../../../../../components/OrderTag'
import { Tag } from '../../../../../components/Tag'
import ProductItemDetailTag from '../../../../../components/ProductItemDetailTag'

interface FacebookOrderHistoryDrawerProps {
  data?: string
  isOpen: boolean
  onClose: () => void
}

const FacebookOrderHistoryDrawer = ({
  data,
  isOpen,
  onClose,
}: FacebookOrderHistoryDrawerProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  const ordersList = useAxios<
    PaginationResponseInterface<IOrder> & { total: OrderSummary }
  >({
    method: 'GET',
    url: '/order',
    params: {
      page: 1,
      perPage: 10,
      recipient: data,
    },
  })

  const onUpdateOrdersListSuccess = useCallback(async () => {
    try {
      await ordersList.mutate()
    } catch (error) {
      message.error('ไม่สามารถดึงข้อมูลรายการสั่งซื้อได้')
    }
  }, [ordersList])

  const columns: TableColumnsType<IOrder & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      align: 'center' as const,
      render: (key: number) => {
        return (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign={'center'}
          >
            {key + 1}
          </Typography>
        )
      },
    },
    {
      title: 'เลขออเดอร์',
      dataIndex: 'code',
      key: 'code',
      align: 'center' as const,
      render: (code: string, order: IOrder) => (
        <Typography
          variant="subtitle1"
          color="text.primary"
          component={Link}
          to={`${PageUrls.Orders.Detail}/${order.objectId}`}
          sx={{ cursor: 'pointer' }}
        >
          {code}
        </Typography>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string, order: IOrder) => {
        return (
          <>
            {order.trackingCode && order.trackingProvider && (
              <Box mb={1}>
                <TrackingProviderImage
                  trackingProvider={
                    order.trackingProvider as OrderShippingProvider
                  }
                />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  textAlign={'center'}
                >
                  {order.trackingCode} {order.providerStatus}
                </Typography>
              </Box>
            )}
            <Box>
              <OrderTag order={order} status={status as EOrderStatus} />
            </Box>
            {order.providerStatusText && (
              <Box pt={1}>
                <Tag color={`success`}>{order.providerStatusText}</Tag>
              </Box>
            )}
          </>
        )
      },
    },
    {
      title: 'สินค้า',
      dataIndex: 'items',
      key: 'items',
      align: 'center' as const,
      render: (items: IOrderItem[]) => (
        <Box minWidth={150}>
          {items?.map((item: IOrderItem, index: number) => {
            return (
              <ProductItemDetailTag
                key={index}
                item={{
                  name: item.productName,
                  quantity: item.quantity,
                  url: item.imageUrl,
                  color: item.meta?.color,
                  size: item.meta?.size,
                }}
              />
            )
          })}
        </Box>
      ),
    },
    {
      title: 'ราคา',
      dataIndex: 'total',
      key: 'total',
      align: 'center' as const,
      render: (total: string, order: IOrder) => (
        <Grid
          container
          flexDirection={'column'}
          textAlign={'center'}
          minWidth={120}
        >
          <Grid item>
            <Typography variant="h5" color="error.main">
              {total.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ marginTop: 1 }}
            >
              {!order?.isCod ? 'เก็บเงินปลายทาง' : 'โอนผ่านธนาคาร'}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {order.items.length} รายการ/{order.quantity} ชิ้น
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)} น.
              </Typography>
            </Grid>
          </Grid>
        )
      },
    },
  ]

  return (
    <Drawer
      anchor={'right'}
      open={isOpen}
      onClose={onClose}
      keepMounted
      PaperProps={{
        style: {
          width: isMobile ? '80%' : '40%',
          maxWidth: isMobile ? '80%' : '40%',
        },
      }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid
          item
          xs={12}
          lg={12}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            ประวัติการสั่งซื้อ
          </Typography>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Table
            size={'middle'}
            columns={columns}
            scroll={{
              x: columns.reduce(
                (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                0,
              ),
            }}
            dataSource={ordersList.data?.records?.map(
              (order: IOrder, index: number) => {
                return {
                  ...order,
                  key: index,
                }
              },
            )}
            bordered
            pagination={false}
          />
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default FacebookOrderHistoryDrawer
